import { formatBytes, formatDateTime, timeAgo, parseUTC, DEFAULT_TZ } from '../utils';

// #190 cierre — el agente late cada 60s (_PROGRESS_HEARTBEAT_INTERVAL_SECONDS).
// Antes del primer latido no hay progress_* todavía; ese primer minuto es
// esperable y se distingue del caso "no está latiendo" (agente caído o
// versión vieja), que vale la pena señalarle al operador.
const FIRST_HEARTBEAT_GRACE_MS = 90 * 1000;

function buildTooltip(r, tz) {
  const parts = [];
  if (r.progress_bytes_processed != null && r.progress_total_bytes) {
    parts.push(`Procesado: ${formatBytes(r.progress_bytes_processed)} de ${formatBytes(r.progress_total_bytes)}`);
  }
  if (r.last_progress_at) {
    parts.push(`Último latido: ${formatDateTime(r.last_progress_at, tz)}`);
  }
  if (r.started_at) {
    parts.push(`Transcurrido: ${timeAgo(r.started_at)}`);
  }
  return parts.join(' — ') || undefined;
}

export function RunProgressLine({ r, tz = DEFAULT_TZ }) {
  if (r.status !== 'running') return null;

  const pct = (r.progress_total_bytes && r.progress_bytes_processed != null)
    ? Math.min(100, Math.round((r.progress_bytes_processed / r.progress_total_bytes) * 100))
    : null;
  const ago = r.last_progress_at ? timeAgo(r.last_progress_at) : null;

  let label;
  if (pct !== null) {
    label = `${pct}% procesado (${formatBytes(r.progress_bytes_processed)} de ${formatBytes(r.progress_total_bytes)})`;
  } else if (ago) {
    label = 'en curso';
  } else {
    const elapsedMs = r.started_at ? Date.now() - parseUTC(r.started_at).getTime() : 0;
    label = elapsedMs < FIRST_HEARTBEAT_GRACE_MS
      ? 'en curso — el progreso aparece al minuto'
      : 'en curso — sin datos de progreso';
  }

  return (
    <div title={buildTooltip(r, tz)} style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginTop: 2 }}>
      {label}
      {ago ? ` — latido ${ago}` : ''}
    </div>
  );
}

// #192 D3, 2.a — badges de carga inicial, agregados AL LADO del badge de
// estado (igual que hoy AnomalyBadge en RunHistoryTab.js), nunca en su
// lugar: el orden de prioridad honeypot > ventana cortada > estado normal
// no cambia, esto se suma siempre después.
function _smallBadge(key, { label, mobileLabel, color, bg, border, title }, isMobile) {
  return (
    <span key={key} title={title} style={{
      display: 'inline-block', padding: '3px 8px', fontSize: 10, fontFamily: 'var(--mono)',
      letterSpacing: 1, textTransform: 'uppercase', borderRadius: 2,
      background: bg, color, border: border || 'none', cursor: 'help',
    }}>
      {isMobile ? (mobileLabel || label) : label}
    </span>
  );
}

const INITIAL_LOAD_FLAG_BADGES = {
  INITIAL_LOAD_STARTED: {
    label: 'INICIO DE CARGA', mobileLabel: 'INICIO',
    color: 'var(--accent)', bg: 'rgba(0,212,255,0.12)',
    title: 'Comenzó la carga inicial',
  },
  INITIAL_LOAD_COMPLETED: {
    label: 'CARGA COMPLETA', mobileLabel: 'COMPLETA',
    color: 'var(--green)', bg: 'rgba(0,229,160,0.12)',
    title: 'La carga inicial terminó; desde acá los backups son incrementales',
  },
  WINDOW_TOO_SHORT: {
    label: 'VENTANA CORTA', mobileLabel: 'VENTANA CORTA',
    color: 'var(--yellow)', bg: 'rgba(255,209,102,0.12)',
    title: 'La ventana configurada no alcanza para cerrar un tramo — conviene ampliarla',
  },
  HIGH_WATER_REGRESSION: {
    label: 'RETROCESO', mobileLabel: 'RETROCESO',
    color: 'var(--red)', bg: 'rgba(255,0,0,0.12)',
    title: 'El agente reportó un avance menor al ya guardado; se ignoró — conviene revisarlo',
  },
};

// #207 parte 2 — error_type de honeypots que el agente 1.19.x puede dejar en un
// run (HONEYPOT_TRIGGERED tiene su propio badge, más abajo). Mismo criterio que
// WINDOW_EXHAUSTED: texto corto, y el mensaje del agente de tooltip.
const HONEYPOT_NOTE_BADGES = {
  PATH_NOT_FOUND: {
    label: 'CARPETA INEXISTENTE', mobileLabel: 'SIN CARPETA',
    title: 'La carpeta respaldada ya no existe',
  },
  HONEYPOT_REARMED: {
    label: 'CARPETA RECREADA', mobileLabel: 'RECREADA',
    title: 'La carpeta fue borrada y recreada; se rearmaron los honeypots',
  },
  HONEYPOT_ADOPTED_UNVERIFIED: {
    label: 'HONEYPOT A REVISAR', mobileLabel: 'A REVISAR',
    title: 'Se adoptó un honeypot con encabezado pero con contenido distinto al esperado: conviene revisarlo',
  },
  HONEYPOT_STATE_REBUILT: {
    label: 'ESTADO RECONSTRUIDO', mobileLabel: 'RECONSTRUIDO',
    title: 'El estado local de honeypots estaba ilegible y se reconstruyó',
  },
  // #194 — no es de honeypots: run cerrado por el detector de zombis del scheduler.
  ZOMBIE_TIMEOUT: {
    label: 'SIN SEÑALES', mobileLabel: 'SIN SEÑAL',
    title: 'El agente dejó de dar señales de vida y el run se cerró automáticamente',
  },
};

function _initialLoadBadges(r, isMobile) {
  const badges = [];
  if (r.initial_load_tranche) {
    // #192 D1b — en una corrida cortada por ventana, initial_load_bytes_in_tranche
    // son bytes PROCESADOS antes del corte, no subidos a ningún snapshot:
    // nunca se suman al acumulado (initial_load_bytes_after) hasta que un
    // tramo cierre de verdad.
    const cut = r.status === 'warning' && r.error_type === 'WINDOW_EXHAUSTED';
    const title = cut
      ? `Procesó ${formatBytes(r.initial_load_bytes_in_tranche)} antes del corte; lo procesado no cuenta para el acumulado hasta que un tramo cierre`
      : `Subió ${formatBytes(r.initial_load_bytes_in_tranche)} en este tramo · acumulado ${formatBytes(r.initial_load_bytes_after)}`;
    badges.push(_smallBadge('tramo', {
      label: 'TRAMO', color: 'var(--muted)', bg: 'var(--bg)', border: '1px solid var(--border)', title,
    }, isMobile));
  }
  (r.initial_load_flags || []).forEach(f => {
    const def = INITIAL_LOAD_FLAG_BADGES[f];
    if (def) badges.push(_smallBadge(f, def, isMobile));
  });
  return badges;
}

export function RunStatusBadge({ r, isMobile, DefaultBadge }) {
  let primary;
  if (r.error_type === 'HONEYPOT_TRIGGERED') {
    primary = (
      <span title={r.error_msg || 'Posible ransomware detectado'} style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '3px 8px', fontSize: 10, fontFamily: 'var(--mono)',
        letterSpacing: 1, borderRadius: 2, textTransform: 'uppercase',
        background: 'rgba(255,0,0,0.15)', color: 'var(--red)', fontWeight: 700,
        cursor: 'help',
      }}>
        ⚠ HONEYPOT
      </span>
    );
  } else if (HONEYPOT_NOTE_BADGES[r.error_type]) {
    const def = HONEYPOT_NOTE_BADGES[r.error_type];
    const isError = r.status === 'error';
    primary = (
      <span title={r.error_msg || def.title} style={{
        display: 'inline-block', padding: '3px 8px', fontSize: 10, fontFamily: 'var(--mono)',
        letterSpacing: 1, textTransform: 'uppercase', borderRadius: 2, cursor: 'help',
        background: isError ? 'rgba(255,77,109,0.12)' : 'rgba(255,209,102,0.12)',
        color: isError ? 'var(--red)' : 'var(--yellow)',
      }}>
        {isMobile ? def.mobileLabel : def.label}
      </span>
    );
  } else if (r.status === 'warning' && r.error_type === 'WINDOW_EXHAUSTED') {
    primary = (
      <span title={r.error_msg || 'Corte planificado por ventana de duración máxima — no es una falla'} style={{
        display: 'inline-block', padding: '3px 8px', fontSize: 10, fontFamily: 'var(--mono)',
        letterSpacing: 1, textTransform: 'uppercase', borderRadius: 2,
        background: 'rgba(255,209,102,0.12)', color: 'var(--yellow)', cursor: 'help',
      }}>
        {isMobile ? 'VENTANA' : 'CORTADO POR VENTANA'}
      </span>
    );
  } else {
    primary = <DefaultBadge r={r} isMobile={isMobile} />;
  }

  const extras = _initialLoadBadges(r, isMobile);
  if (extras.length === 0) return primary;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
      {primary}
      {extras}
    </span>
  );
}
